import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  Users, 
  BookOpen, 
  Phone, 
  Mail, 
  MapPin,
  Calendar,
  ArrowRight,
  Star,
  Award,
  Target,
  GraduationCap
} from 'lucide-react';
import { useConfig } from '../contexts/ConfigContext';
import { db } from '../database/supabase';
import { asset } from '../utils/asset';

const FALLBACK_ANNOUNCEMENTS = [
  {
    id: 1,
    title: "New Academic Year Guidance Services",
    content: "We're excited to welcome all students for the new academic year. Our guidance services are now available with extended hours.",
    date: "2026-01-15",
    priority: "high"
  },
  {
    id: 2,
    title: "Career Counseling Workshop",
    content: "Join us for a comprehensive career counseling workshop this Friday at 2:00 PM in the guidance center.",
    date: "2026-01-10",
    priority: "normal"
  },
  {
    id: 3,
    title: "Senior High School Exit Forms Available",
    content: "All graduating students must complete their exit forms before the end of the month.",
    date: "2026-01-08",
    priority: "normal"
  }
];

const HomePage = () => {
  const { config } = useConfig();
  const [announcements, setAnnouncements] = useState(FALLBACK_ANNOUNCEMENTS);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await db.getAnnouncements(true);
        if (error || !data?.length || cancelled) return;
        setAnnouncements(
          data.slice(0, 3).map((a) => ({
            id: a.id,
            title: a.title,
            content: a.content,
            date: a.published_at || a.created_at,
            priority: a.priority || 'normal',
          }))
        );
      } catch (err) {
        console.warn('Using fallback announcements:', err.message || err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Sample staff data - in real app, this would come from database
  const staffMembers = [
    {
      id: 1,
      name: "Sr. Neddie B. Nueva, DC",
      title: "Guidance Coordinator",
      photo: asset("/images/sorneddie.jpg"),
      email: "nuevasr.neddiedc@gmail.com",
      education: "BSED – Guidance and Counseling / Santa Isabel College, MAEd Guidance and Counseling / Philippine Normal University"
    },
    {
      id: 2,
      name: "Casimira D. Jarabo, Ph.D.",
      title: "VP for Student Development, Wellness and External Linkages",
      photo: asset("/images/casimira.jpg"),
      email: "slmcsi.sdwel2025@gmail.com",
      education: "Doctorate Degree / Annunciation College of Bacon Sorsogon Unit, Inc."
    },
    {
      id: 3,
      name: "Rena Giba Balbalosa",
      title: "Guidance Associate, BED SHS",
      photo: asset("/images/rena.jpg"),
      email: "slmcsrgb@gmail.com",
      education: "Bachelor of Science in Psychology | Bicol University"
    },
    {
      id: 4,
      name: "Ramil C. Esmeria",
      title: "Guidance Associate - BED (K-10)",
      photo: asset("/images/ramil.jpg"),
      email: "ramilesmeria66@gmail.com",
      education: "Bachelor of Science in Psychology & Master of Arts in Counseling"
    },
    {
      id: 5,
      name: "Antonette Honrubia Bon",
      title: "HED Guidance Associate",
      photo: asset("/images/antonette.jpg"),
      email: "bon.slmci@gmail.com",
      education: "Bachelor of Science in Psychology | Bicol University"
    }
  ];

  const services = [
    {
      icon: BookOpen,
      title: "Guidance Curriculum",
      description: "Structured developmental experiences presented systematically through group activities from Kindergarten through College level.",
      link: "/services#curriculum"
    },
    {
      icon: Target,
      title: "Individual Planning",
      description: "Activities that help all students explore, plan, monitor and manage their own learning as well as their personal, academic and career development.",
      link: "/services#planning"
    },
    {
      icon: Heart,
      title: "Responsive Services",
      description: "Activities to meet the immediate needs and concerns of students, whether these needs require counseling, consultation, referral, or information.",
      link: "/services#responsive"
    },
    {
      icon: Users,
      title: "System Support",
      description: "Management activities that establish, maintain, and enhance the total guidance program.",
      link: "/services#system"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-blue-800 to-blue-900 text-white py-20">
        {/* Banner Background */}
        <div className="absolute inset-0 opacity-20">
          <img 
            src={asset("/images/banner.jpg")} 
            alt="Banner"
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container-custom relative z-10">
          {/* Logos on both sides */}
          <div className="flex justify-between items-start mb-8">
            <img 
              src={asset("/images/logo_school_new.png")} 
              alt="School Logo"
              className="h-24 w-auto"
              onError={(e) => e.target.style.display = 'none'}
            />
            <img 
              src={asset("/images/logo_guidance_new.png")} 
              alt="Guidance Center Logo"
              className="h-24 w-auto"
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
          
          <div className="text-center max-w-4xl mx-auto">
            
            <h1 className="text-5xl font-bold mb-6">
              Welcome to {config?.site?.name || 'Guidance Center'}
            </h1>
            <p className="text-xl mb-8 text-blue-100">
              Empowering students to achieve their full potential through comprehensive guidance services, 
              academic support, and personal development programs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/services" className="btn-secondary">
                Explore Our Services
              </Link>
              <Link to="/forms" className="btn-outline border-white text-white hover:bg-white hover:text-blue-800">
                Access Forms
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values Section */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">Our Foundation</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Built on strong principles that guide our commitment to student success and well-being.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {/* Vision */}
            <div className="card text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Our Vision</h3>
              <p className="text-gray-600">
                SLMCS Guidance and Counseling Office aims to develop well-adjusted and holistically formed individuals equipped with necessary knowledge, attitude, values and skills to become mature and socially responsible individuals who will promote a just and humane society.
              </p>
            </div>

            {/* Mission */}
            <div className="card text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="w-8 h-8 text-blue-800" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Our Mission</h3>
              <p className="text-gray-600">
                To realize this vision, we commit ourselves to provide assistance in a holistic attainment and development of necessary proficiencies and life skills needed by the students through quality programs and services.
              </p>
            </div>

            {/* Values */}
            <div className="card text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-800">Core Values</h3>
              <ul className="text-gray-600 text-left space-y-1">
                <li>• Love for the Poor</li>
                <li>• Simplicity of Lifestyle</li>
                <li>• Commitment to Academic Excellence</li>
                <li>• Vincentian Excellence</li>
                <li>• Professional Competence</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">Our Services</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive guidance services designed to support every aspect of your educational journey.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <div key={index} className="card-hover text-center group">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:bg-blue-200 transition-colors">
                  <service.icon className="w-8 h-8 text-blue-800" />
                </div>
                <h3 className="text-xl font-semibold mb-4 text-gray-800">{service.title}</h3>
                <p className="text-gray-600 mb-6">{service.description}</p>
                <Link 
                  to={service.link} 
                  className="inline-flex items-center text-blue-800 hover:text-blue-900 font-medium"
                >
                  Learn More <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/services" className="btn-primary">
              View All Services
            </Link>
          </div>
        </div>
      </section>

      {/* Staff Profiles */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">SLMCS Guidance Personnel</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our dedicated team of professional counselors and advisors are here to support your success.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-8">
            {staffMembers.map((staff) => (
              <div key={staff.id} className="card text-center">
                <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden bg-gray-200">
                  <img 
                    src={staff.photo} 
                    alt={staff.name}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      e.target.src = asset('/images/default-staff.jpg');
                    }}
                  />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-gray-800">{staff.name}</h3>
                <p className="text-blue-800 font-medium text-sm mb-2">{staff.title}</p>
                <p className="text-gray-600 text-xs mb-2">{staff.email}</p>
                <p className="text-gray-500 text-xs">{staff.education}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/staff" className="btn-outline">
              Meet All Staff Members
            </Link>
          </div>
        </div>
      </section>

      {/* Announcements */}
      <section className="py-16 bg-gray-50">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">Latest Announcements</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Stay updated with the latest news and important information from our guidance center.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {announcements.map((announcement) => (
              <div key={announcement.id} className="card">
                <div className="flex items-center justify-between mb-3">
                  <span className={`status-badge ${
                    announcement.priority === 'high' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'
                  }`}>
                    {announcement.priority === 'high' ? 'Important' : 'News'}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center">
                    <Calendar className="w-4 h-4 mr-1" />
                    {new Date(announcement.date).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="text-lg font-semibold mb-3 text-gray-800">{announcement.title}</h3>
                <p className="text-gray-600">{announcement.content}</p>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/announcements" className="btn-outline">
              View All Announcements
            </Link>
          </div>
        </div>
      </section>

      {/* Quick Actions */}
      <section className="py-16 bg-blue-800 text-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Quick Actions</h2>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Access the most commonly used services and forms with just one click.
            </p>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <Link to="/teacher-evaluation" className="bg-white bg-opacity-10 p-6 rounded-lg text-center hover:bg-opacity-20 transition-colors">
              <GraduationCap className="w-8 h-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Teacher Evaluation</h3>
              <p className="text-sm text-blue-100">Evaluate your teachers</p>
            </Link>
            
            <Link to="/forms/student-needs-assessment" className="bg-white bg-opacity-10 p-6 rounded-lg text-center hover:bg-opacity-20 transition-colors">
              <BookOpen className="w-8 h-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Student Assessment</h3>
              <p className="text-sm text-blue-100">Complete your needs assessment</p>
            </Link>
            
            <Link to="/contact" className="bg-white bg-opacity-10 p-6 rounded-lg text-center hover:bg-opacity-20 transition-colors">
              <Phone className="w-8 h-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">Schedule Appointment</h3>
              <p className="text-sm text-blue-100">Book a counseling session</p>
            </Link>
            
            <Link to="/forms" className="bg-white bg-opacity-10 p-6 rounded-lg text-center hover:bg-opacity-20 transition-colors">
              <Heart className="w-8 h-8 mx-auto mb-3" />
              <h3 className="font-semibold mb-2">All Forms</h3>
              <p className="text-sm text-blue-100">Access all available forms</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="section-title">Get in Touch</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              We're here to help. Reach out to us through any of the following channels.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Phone</h3>
              <p className="text-gray-600">{config?.site?.phone || '(555) 123-4567'}</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Email</h3>
              <p className="text-gray-600">slmcs.gcs@gmail.com</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <MapPin className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Visit Us</h3>
              <p className="text-gray-600">
                Burgos Street, Talisay<br />
                4700 Sorsogon, Sorsogon
              </p>
            </div>
          </div>
          
          <div className="text-center mt-12">
            <Link to="/contact" className="btn-primary">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;